/**
 * Delivery estimate for Order Tracker V2 (window.OrderDeliveryEstimate) — same working-day maths as the PDP shipping.js.liquid, 1-man only.
 * Settings from data-delivery-* on #order-tracking. API dates = UTC calendar day (like tracker-v2.js); today / cut-off = Europe/London.
 */
(function (global) {
    const BUSINESS_TZ = 'Europe/London';
    const DAY_MS = 24 * 60 * 60 * 1000;
    const MONTH_NAMES_SHORT_FALLBACK = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    function getRoot() {
        return document.getElementById('order-tracking');
    }

    function toInt(value, fallback) {
        const n = parseInt(value, 10);
        return Number.isFinite(n) ? n : fallback;
    }

    function pad(n) {
        return String(n).padStart(2, '0');
    }

    function utcDay(year, month, day) {
        return new Date(Date.UTC(year, month, day));
    }

    function validUtcDay(year, month, day) {
        const date = utcDay(year, month, day);
        if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month || date.getUTCDate() !== day) return null;
        return date;
    }

    function dayKey(date) {
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    }

    function parseDate(value) {
        if (value == null || value === '') return null;
        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) return null;
            return utcDay(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
        }

        const str = String(value).trim();
        const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (dmy) return validUtcDay(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10));

        const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (ymd) return validUtcDay(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));

        const isoDay = str.match(/^(\d{4})-(\d{2})-(\d{2})T/);
        if (isoDay) {
            // Reject impossible days (2026-02-31) — new Date() would roll them over.
            if (!validUtcDay(parseInt(isoDay[1], 10), parseInt(isoDay[2], 10) - 1, parseInt(isoDay[3], 10))) return null;
            const date = new Date(str);
            if (Number.isNaN(date.getTime())) return null;
            return utcDay(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        }
        return null;
    }

    function toIso(value) {
        const date = parseDate(value);
        return date ? `${dayKey(date)}T00:00:00Z` : '';
    }

    function toDMY(value) {
        const date = parseDate(value);
        return date ? `${pad(date.getUTCDate())}-${pad(date.getUTCMonth() + 1)}-${date.getUTCFullYear()}` : '';
    }

    function londonNow(now = new Date()) {
        const parts = {};
        new Intl.DateTimeFormat('en-GB', {
            timeZone: BUSINESS_TZ,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        })
            .formatToParts(now)
            .forEach((part) => {
                parts[part.type] = part.value;
            });
        return {
            date: utcDay(toInt(parts.year, 1970), toInt(parts.month, 1) - 1, toInt(parts.day, 1)),
            seconds: toInt(parts.hour, 0) * 3600 + toInt(parts.minute, 0) * 60 + toInt(parts.second, 0),
        };
    }

    function today(now = new Date()) {
        return londonNow(now).date;
    }

    function parseCutOff(value) {
        const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;
        return parseInt(match[1], 10) * 3600 + parseInt(match[2], 10) * 60;
    }

    function getConfig() {
        const ds = getRoot()?.dataset || {};
        const daysOff = new Set();
        String(ds.deliveryDaysOff || '')
            .split(',')
            .forEach((value) => {
                const date = parseDate(value);
                if (date) daysOff.add(dayKey(date));
            });
        // Fallbacks = settings_schema.json defaults.
        return {
            cutOff: parseCutOff(ds.deliveryDispatchCutoff),
            daysOff,
            inStockTransportDays: toInt(ds.deliveryInStockTransportDays, 1),
            inStockWindowDays: toInt(ds.deliveryInStockWindowDays, 2),
            preOrderWindowDays: toInt(ds.deliveryPreOrderWindowDays, 5),
        };
    }

    function addDays(date, days) {
        return new Date(date.getTime() + days * DAY_MS);
    }

    function isWorkingDay(date, daysOff) {
        const weekday = date.getUTCDay();
        return weekday !== 0 && weekday !== 6 && !daysOff.has(dayKey(date));
    }

    function addWorkingDays(date, workingDays, daysOff) {
        if (workingDays === 0) return addWorkingDays(addWorkingDays(date, 1, daysOff), -1, daysOff);

        const direction = workingDays < 0 ? -1 : 1;
        let remaining = workingDays;
        let result = date;
        while (remaining !== 0) {
            result = addDays(result, direction);
            if (isWorkingDay(result, daysOff)) remaining -= direction;
        }
        return result;
    }

    function monthNamesShort() {
        const names = global.orderTrackerTranslations?.monthNamesShort;
        return Array.isArray(names) && names.length === 12 ? names : MONTH_NAMES_SHORT_FALLBACK;
    }

    function formatDateRange(start, end) {
        const s = parseDate(start);
        const e = parseDate(end);
        if (!s || !e) return '';

        if (s.getTime() === e.getTime()) {
            return `${pad(s.getUTCDate())}/${pad(s.getUTCMonth() + 1)}/${s.getUTCFullYear()}`;
        }

        const months = monthNamesShort();
        const currentYear = today().getUTCFullYear();
        const yearSuffix = (year) => (year !== currentYear ? ` ${year}` : '');
        const sDay = s.getUTCDate();
        const eDay = e.getUTCDate();
        const sMonth = months[s.getUTCMonth()];
        const eMonth = months[e.getUTCMonth()];
        const sYear = s.getUTCFullYear();
        const eYear = e.getUTCFullYear();

        if (sYear === eYear && s.getUTCMonth() === e.getUTCMonth()) {
            return `${sDay}-${eDay} ${sMonth}${yearSuffix(sYear)}`;
        }
        if (sYear === eYear) {
            return `${sDay} ${sMonth}${yearSuffix(sYear)} - ${eDay} ${eMonth}${yearSuffix(eYear)}`;
        }
        return `${sDay} ${sMonth} ${sYear} - ${eDay} ${eMonth} ${eYear}`;
    }

    /**
     * PDP calculateDateRangeForProduct() for 1-man delivery. availabilityDate = pre-order date when not in stock.
     * @param {{ inStockNow?: boolean, availabilityDate?: string|Date, now?: Date }} options
     */
    function calculate({ inStockNow = false, availabilityDate = null, now = new Date() } = {}) {
        const preOrder = inStockNow ? null : parseDate(availabilityDate);
        if (!inStockNow && !preOrder) {
            return { success: false, dateRangeString: null };
        }

        const cfg = getConfig();
        const london = londonNow(now);

        // getEarliestAvailableDate()
        let start = preOrder && preOrder.getTime() > london.date.getTime() ? preOrder : london.date;

        // adjustForCutoffTime() — on a weekend / day off, addWorkingDays(today, 0) is the previous working day, so it's always past cut-off.
        if (start.getTime() === london.date.getTime()) {
            const dispatchDay = addWorkingDays(london.date, 0, cfg.daysOff);
            const pastCutOff =
                dispatchDay.getTime() < london.date.getTime() ||
                (cfg.cutOff != null && london.seconds > cfg.cutOff);
            if (pastCutOff) start = addWorkingDays(start, 1, cfg.daysOff);
        }

        // calculateDeliveryDates()
        let minDeliveryDate = start;
        let maxDeliveryDate;
        if (!preOrder) {
            minDeliveryDate = addWorkingDays(start, cfg.inStockTransportDays, cfg.daysOff);
            maxDeliveryDate = addWorkingDays(minDeliveryDate, cfg.inStockWindowDays, cfg.daysOff);
        } else {
            maxDeliveryDate = addWorkingDays(start, cfg.preOrderWindowDays, cfg.daysOff);
        }

        return {
            success: true,
            inStockNow: !preOrder,
            dateRangeString: formatDateRange(minDeliveryDate, maxDeliveryDate),
            minDeliveryDate,
            maxDeliveryDate,
            minDeliveryDateIso: toIso(minDeliveryDate),
            maxDeliveryDateIso: toIso(maxDeliveryDate),
        };
    }

    global.OrderDeliveryEstimate = {
        calculate,
        parseDate,
        formatDateRange,
        toIso,
        toDMY,
        today,
    };
})(window);
